import React from "react";
import {useTranslation} from "react-i18next";
import {Dropdown} from "primereact/dropdown";
import {MultiSelect} from "primereact/multiselect";
import {Calendar} from "primereact/calendar";
import {InputSwitch} from "primereact/inputswitch";
import {InputTextarea} from "primereact/inputtextarea";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Checkbox} from "primereact/checkbox";
import {Message} from "primereact/message";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {Badge} from "primereact/badge";
import {AppContext} from "../../../HomePage";
import {EmailCampaignService} from "../../../service/EmailCampaignService";
import {
    DEFAULT_CRITERIA, COUNTRY_OPTIONS, CUSTOMER_STATUS_OPTIONS,
    SUBSCRIPTION_TYPE_OPTIONS, TAG_OPTIONS, DATE_QUICK_OPTIONS,
} from "./recipientFilters";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function parseManual(raw) {
    const out = [];
    const seen = new Set();
    (raw || '').split(/[,;\n]/).forEach((tok) => {
        const e = tok.trim().toLowerCase();
        if (e && EMAIL_RE.test(e) && !seen.has(e)) {
            seen.add(e);
            out.push(e);
        }
    });
    return out;
}

// Translate a localized-or-literal option label.
function optLabel(t, label) {
    return label && label.startsWith('email_campaign.') ? t(label) : label;
}

// The main screen owns the recipient textbox (the source of truth). The dialog
// is a discovery tool: filter users, pick them (and their sub-profiles), and
// "Add selected" appends the chosen addresses into the main textbox.
export default function RecipientBuilder({onChange}) {
    const {t} = useTranslation();
    const {toast, setError} = React.useContext(AppContext);
    const service = React.useMemo(() => new EmailCampaignService(), []);

    const [recipients, setRecipients] = React.useState('');  // the master list
    const [criteria, setCriteria] = React.useState(DEFAULT_CRITERIA);
    const [lastLoginMode, setLastLoginMode] = React.useState('any');
    const [signupMode, setSignupMode] = React.useState('any');
    const [users, setUsers] = React.useState([]);            // filter results
    const [selected, setSelected] = React.useState(new Set()); // checked emails
    const [truncated, setTruncated] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [savedLists, setSavedLists] = React.useState([]);
    const [selectedListId, setSelectedListId] = React.useState(null);
    const [saveDialog, setSaveDialog] = React.useState(false);
    const [listName, setListName] = React.useState('');

    const setC = (patch) => setCriteria((prev) => ({...prev, ...patch}));

    const validRecipients = React.useMemo(() => parseManual(recipients), [recipients]);

    // Emit the recipient list to the parent (deduped + validated).
    React.useEffect(() => {
        onChange({recipientsRaw: validRecipients.join('\n'), count: validRecipients.length, criteria});
    }, [validRecipients, criteria, onChange]);

    // Every email in the current result set (users + their sub-profiles when on).
    const emailsOf = React.useCallback((list, withSubs) => {
        const out = [];
        (list || []).forEach((u) => {
            out.push(u.email);
            if (withSubs) (u.sub_profiles || []).forEach((p) => out.push(p.email));
        });
        return out;
    }, []);

    const currentEmails = React.useMemo(
        () => emailsOf(users, criteria.include_sub_profiles),
        [users, criteria.include_sub_profiles, emailsOf]);

    // At least one narrowing filter must be set before we hit the backend, so
    // opening the dialog does not pull the entire user base by default.
    const hasActiveFilter = React.useMemo(() => {
        const c = criteria;
        return !!c.country
            || (c.customer_status && c.customer_status.length > 0)
            || (c.tags && c.tags.length > 0)
            || (c.subscription_types && c.subscription_types.length > 0)
            || c.last_login_within_months != null || !!c.last_login_from || !!c.last_login_to
            || c.signed_up_within_months != null || !!c.signed_up_from || !!c.signed_up_to;
    }, [criteria]);

    // Resolve the filter results whenever criteria changes (debounced), but only
    // while the dialog is open and a filter is set. New results start fully
    // checked.
    React.useEffect(() => {
        if (!open || !hasActiveFilter) {
            setUsers([]);
            setSelected(new Set());
            setTruncated(false);
            return undefined;
        }
        const handle = setTimeout(() => {
            setLoading(true);
            service.resolveRecipients(criteria)
                .then((data) => {
                    const list = data.users || [];
                    setUsers(list);
                    setSelected(new Set(emailsOf(list, criteria.include_sub_profiles)));
                    setTruncated(!!data.truncated);
                })
                .catch((e) => { setError(e); setError(null); })
                .finally(() => setLoading(false));
        }, 400);
        return () => clearTimeout(handle);
    }, [criteria, service, setError, emailsOf, open, hasActiveFilter]);

    const toggle = (email) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(email)) next.delete(email); else next.add(email);
        return next;
    });

    const allSelected = currentEmails.length > 0 && currentEmails.every((e) => selected.has(e));
    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(currentEmails));

    // Always keep rows with sub-profiles expanded (no collapse control).
    // With dataKey="email", PrimeReact expects expandedRows as a map keyed by
    // the data key, not an array.
    const expandedRows = React.useMemo(() => {
        if (!criteria.include_sub_profiles) return null;
        const map = {};
        users.forEach((u) => { if ((u.sub_profiles || []).length > 0) map[u.email] = true; });
        return map;
    }, [users, criteria.include_sub_profiles]);

    const loadSavedLists = React.useCallback(() => {
        service.listRecipientLists()
            .then((data) => setSavedLists((data.items || []).map(
                (i) => ({label: i.name + ' (' + i.count + ')', value: i.id}))))
            .catch((e) => { setError(e); setError(null); });
    }, [service, setError]);

    React.useEffect(() => { loadSavedLists(); }, [loadSavedLists]);

    const appendEmails = (emails) => {
        const merged = parseManual(recipients).concat(emails);
        const deduped = Array.from(new Set(merged.map((e) => (e || '').trim().toLowerCase())))
            .filter((e) => e && EMAIL_RE.test(e));
        setRecipients(deduped.join('\n'));
    };

    const addSelected = () => {
        const emails = Array.from(selected);
        appendEmails(emails);
        setOpen(false);
        toast.current.show({severity: 'success', summary: t('email_campaign.added_selected', {count: emails.length})});
    };

    const doSaveList = () => {
        const name = listName.trim();
        if (!name) return;
        service.saveRecipientList(name, validRecipients, criteria)
            .then((data) => {
                toast.current.show({severity: 'success', summary: t('email_campaign.list_saved', {count: (data && data.count) || 0})});
                setSaveDialog(false);
                setListName('');
                loadSavedLists();
            })
            .catch((e) => { setError(e); setError(null); });
    };

    const doLoadList = (id) => {
        setSelectedListId(id);
        if (!id) return;
        service.getRecipientList(id)
            .then((data) => {
                appendEmails(data.emails || []);
                toast.current.show({severity: 'success', summary: t('email_campaign.list_loaded', {count: (data.emails || []).length})});
            })
            .catch((e) => { setError(e); setError(null); });
    };

    const deleteList = (id) => {
        if (!id) return;
        service.deleteRecipientList(id)
            .then(() => {
                toast.current.show({severity: 'success', summary: t('email_campaign.list_deleted')});
                if (selectedListId === id) setSelectedListId(null);
                loadSavedLists();
            })
            .catch((e) => { setError(e); setError(null); });
    };

    // Each saved-list dropdown item shows its name plus a trash icon that
    // deletes that specific list (without selecting it).
    const savedListItemTemplate = (option) => {
        if (!option) return null;
        return (
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}>
                <span>{option.label}</span>
                <i className="pi pi-trash" style={{color: '#d9534f', cursor: 'pointer', marginLeft: '.5em'}}
                   title={t('email_campaign.delete_list')}
                   onClick={(e) => { e.stopPropagation(); deleteList(option.value); }}/>
            </div>
        );
    };

    const dateFilter = (label, mode, setMode, withinKey, fromKey, toKey) => (
        <div className="p-field p-col-12 p-md-6">
            <label>{t(label)}</label>
            <Dropdown value={mode} options={DATE_QUICK_OPTIONS.map((o) => ({label: optLabel(t, o.label), value: o.value}))}
                      onChange={(e) => {
                          setMode(e.value);
                          if (e.value === 'any') setC({[withinKey]: null, [fromKey]: null, [toKey]: null});
                          else if (e.value === 'custom') setC({[withinKey]: null});
                          else setC({[withinKey]: e.value, [fromKey]: null, [toKey]: null});
                      }}/>
            {mode === 'custom' &&
                <div style={{display: 'flex', gap: '.5em', marginTop: '.5em'}}>
                    <Calendar placeholder={t('email_campaign.date_from')} dateFormat="yy-mm-dd"
                              value={criteria[fromKey] ? new Date(criteria[fromKey]) : null}
                              onChange={(e) => setC({[fromKey]: e.value ? e.value.toISOString().slice(0, 10) : null})}/>
                    <Calendar placeholder={t('email_campaign.date_to')} dateFormat="yy-mm-dd"
                              value={criteria[toKey] ? new Date(criteria[toKey]) : null}
                              onChange={(e) => setC({[toKey]: e.value ? e.value.toISOString().slice(0, 10) : null})}/>
                </div>}
        </div>
    );

    const headerCheckbox = () => <Checkbox checked={allSelected} onChange={toggleAll}/>;
    const userCheckbox = (row) => <Checkbox checked={selected.has(row.email)} onChange={() => toggle(row.email)}/>;
    const nameBody = (row) => row.name || '';
    const rowExpansion = (row) => (
        <div style={{padding: '.25em 0 .5em 3.5em'}}>
            <div style={{fontSize: '12px', color: '#8a8a8a', marginBottom: '.25em'}}>
                {t('email_campaign.sub_profiles_label')}
            </div>
            {(row.sub_profiles || []).map((p) => (
                <div key={p.email} style={{display: 'flex', alignItems: 'center', gap: '.5em', padding: '.15em 0'}}>
                    <Checkbox checked={selected.has(p.email)} onChange={() => toggle(p.email)}/>
                    <span>{p.email}{p.name ? ' — ' + p.name : ''}</span>
                </div>
            ))}
        </div>
    );

    return (
        <div className="p-field">
            <div style={{display: 'flex', alignItems: 'center', gap: '.5em', marginBottom: '.35em'}}>
                <label style={{margin: 0}}>{t('email_campaign.recipients')}</label>
                <Badge value={validRecipients.length} severity="info" size="large"/>
            </div>

            <div style={{display: 'flex', gap: '1em', alignItems: 'flex-start', flexWrap: 'wrap'}}>
                <InputTextarea rows={7} value={recipients} style={{flex: '1 1 320px'}}
                               placeholder={t('email_campaign.recipients_hint')}
                               onChange={(e) => setRecipients(e.target.value)}/>
                <div style={{display: 'flex', flexDirection: 'column', gap: '.5em', width: '230px'}}>
                    <Button label={t('email_campaign.select_recipients')} icon="pi pi-filter"
                            className="p-button-outlined" style={{width: '100%'}} onClick={() => setOpen(true)}/>
                    <Dropdown value={selectedListId} options={savedLists} style={{width: '100%'}}
                              placeholder={t('email_campaign.saved_lists')}
                              itemTemplate={savedListItemTemplate} onChange={(e) => doLoadList(e.value)}/>
                    <Button label={t('email_campaign.save_list')} icon="pi pi-save" className="p-button-outlined"
                            style={{width: '100%'}} disabled={validRecipients.length === 0}
                            onClick={() => setSaveDialog(true)}/>
                </div>
            </div>

            <Dialog header={t('email_campaign.select_recipients')} visible={open} maximizable
                    style={{width: '85vw'}} contentStyle={{paddingTop: '1em'}} onHide={() => setOpen(false)}
                    footer={
                        <div style={{display: 'flex', alignItems: 'center', gap: '.5em'}}>
                            <span style={{marginRight: 'auto', display: 'inline-flex', alignItems: 'center', gap: '.4em',
                                          background: '#eef2ff', color: '#3730a3', borderRadius: '999px',
                                          padding: '.25em .8em', fontWeight: 600, fontSize: '13px'}}>
                                <i className="pi pi-users" style={{fontSize: '12px'}}/>
                                {t('email_campaign.selected_count', {count: selected.size})}
                            </span>
                            <Button label={t('email_campaign.cancel')} icon="pi pi-times"
                                    className="p-button-text p-button-secondary" onClick={() => setOpen(false)}/>
                            <Button label={t('email_campaign.add_selected')} icon="pi pi-check"
                                    className="p-button-success" disabled={selected.size === 0} onClick={addSelected}/>
                        </div>
                    }>
                <div style={{background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '10px',
                             padding: '1rem 1rem 0.25rem', marginBottom: '1em'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '.5em', marginBottom: '.75em',
                             color: '#495057', fontWeight: 600}}>
                    <i className="pi pi-filter"/>{t('email_campaign.recipients_filters')}
                </div>
                <div className="p-grid p-fluid">
                    <div className="p-field p-col-12 p-md-4">
                        <label>{t('email_campaign.country_label')}</label>
                        <Dropdown value={criteria.country}
                                  options={COUNTRY_OPTIONS.map((o) => ({label: optLabel(t, o.label), value: o.value}))}
                                  onChange={(e) => setC({country: e.value})}/>
                    </div>
                    <div className="p-field p-col-12 p-md-4">
                        <label>{t('email_campaign.customer_status_label')}</label>
                        <MultiSelect value={criteria.customer_status}
                                     options={CUSTOMER_STATUS_OPTIONS.map((o) => ({label: optLabel(t, o.label), value: o.value}))}
                                     onChange={(e) => setC({customer_status: e.value})}/>
                    </div>
                    <div className="p-field p-col-12 p-md-4">
                        <label>{t('email_campaign.subscription_type_label')}</label>
                        <MultiSelect value={criteria.subscription_types} options={SUBSCRIPTION_TYPE_OPTIONS}
                                     onChange={(e) => setC({subscription_types: e.value})}/>
                    </div>
                    <div className="p-field p-col-12 p-md-4">
                        <label>{t('email_campaign.tags_label')}</label>
                        <MultiSelect value={criteria.tags} options={TAG_OPTIONS} filter
                                     onChange={(e) => setC({tags: e.value})}/>
                    </div>
                    {dateFilter('email_campaign.last_login_label', lastLoginMode, setLastLoginMode,
                        'last_login_within_months', 'last_login_from', 'last_login_to')}
                    {dateFilter('email_campaign.signup_label', signupMode, setSignupMode,
                        'signed_up_within_months', 'signed_up_from', 'signed_up_to')}
                    <div className="p-field p-col-12" style={{display: 'flex', alignItems: 'center', gap: '.5em'}}>
                        <InputSwitch checked={criteria.include_sub_profiles}
                                     onChange={(e) => setC({include_sub_profiles: e.value})}/>
                        <label style={{margin: 0}}>{t('email_campaign.include_sub_profiles')}</label>
                    </div>
                </div>
                </div>

                {!hasActiveFilter &&
                    <Message severity="info" text={t('email_campaign.filter_prompt')}/>}
                {truncated &&
                    <Message severity="warn" text={t('email_campaign.truncated_notice', {count: users.length})}/>}

                {hasActiveFilter &&
                <DataTable value={users} loading={loading} paginator rows={25} dataKey="email"
                           className="p-datatable-striped p-datatable-sm" rowHover responsiveLayout="scroll"
                           expandedRows={expandedRows} onRowToggle={() => {}}
                           rowExpansionTemplate={rowExpansion}>
                    {criteria.include_sub_profiles && <Column expander style={{width: '3rem'}}/>}
                    <Column header={headerCheckbox} body={userCheckbox} style={{width: '3rem'}}/>
                    <Column header={t('email_campaign.recipient_name')} body={nameBody}/>
                    <Column field="email" header="Email"/>
                </DataTable>}
            </Dialog>

            <Dialog header={t('email_campaign.save_list')} visible={saveDialog} style={{width: '360px'}}
                    onHide={() => setSaveDialog(false)}
                    footer={<Button label={t('email_campaign.save_list')} icon="pi pi-save" onClick={doSaveList}/>}>
                <span className="p-float-label" style={{marginTop: '1em', display: 'block'}}>
                    <InputText value={listName} onChange={(e) => setListName(e.target.value)} style={{width: '100%'}}
                               placeholder={t('email_campaign.save_list_name')}/>
                </span>
            </Dialog>
        </div>
    );
}
